import { useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TableSkeleton } from '@/components/ui/skeleton';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { useAuthorizationStore } from '../store';
import { createUserRolesColumns } from './user-roles-columns';
import { UserRoleSheet } from './UserRoleSheet';
import { UserRoleHistoryModal } from './UserRoleHistoryModal';

export function UserRolesTable() {
  const {
    usersWithRoles,
    roles,
    selectedUserIds,
    batchSelectedRoleIds,
    isLoadingUsers,
    isLoadingRoles,
    fetchUsersWithRoles,
    fetchRoles,
    clearUserSelection,
    openUserRoleSheet,
    isApplyingBatchRoles,
    toggleBatchRoleSelection,
    clearBatchRoleSelection,
    applyBatchRoles,
  } = useAuthorizationStore();

  useEffect(() => {
    fetchUsersWithRoles();
    fetchRoles();
  }, [fetchUsersWithRoles, fetchRoles]);

  const columns = useMemo(
    () => createUserRolesColumns(selectedUserIds),
    [selectedUserIds]
  );

  const table = useReactTable({
    data: usersWithRoles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoadingUsers || isLoadingRoles) {
    return <TableSkeleton rows={8} cols={7} className="py-2" />;
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            className="pl-8"
            disabled
            title="Search functionality coming soon"
          />
        </div>
      </div>

      {/* Batch Action Bar */}
      <PermissionGuard permission={PERMISSIONS.AUTH_EDIT_USER_ROLE}>
      {selectedUserIds.length > 0 && (
        <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{selectedUserIds.length} selected</Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearUserSelection}
              className="h-7 px-2"
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Add Roles:</span>
            <div className="flex flex-wrap gap-2 max-w-md">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-1.5 px-2 py-1 rounded border bg-background cursor-pointer hover:bg-muted/50"
                >
                  <Checkbox
                    checked={batchSelectedRoleIds.includes(role.id)}
                    onCheckedChange={() => toggleBatchRoleSelection(role.id)}
                  />
                  <span className="text-sm">{role.name}</span>
                </label>
              ))}
            </div>
            {batchSelectedRoleIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearBatchRoleSelection}
                className="h-7 px-2"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="sm"
              disabled={batchSelectedRoleIds.length === 0 || isApplyingBatchRoles}
              onClick={async () => {
                try {
                  const success = await applyBatchRoles();
                  if (success) {
                    toast.success(`Roles applied to ${selectedUserIds.length} user(s)`);
                  }
                } catch {
                  toast.error('Failed to apply batch roles');
                }
              }}
            >
              {isApplyingBatchRoles && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply ({batchSelectedRoleIds.length})
            </Button>
          </div>
        </div>
      )}
      </PermissionGuard>

      {/* Table — flat: row dividers only, no outer box */}
      <div>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={
                    selectedUserIds.includes(row.original.userId) && 'selected'
                  }
                  className="cursor-pointer"
                  onClick={() => openUserRoleSheet(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      onClick={(e) => {
                        // Prevent row click for checkbox and actions columns
                        if (cell.column.id === 'select' || cell.column.id === 'actions') {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination - Placeholder */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          Showing {usersWithRoles.length} user(s)
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" disabled>
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button variant="outline" size="sm" disabled>
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* User Role Sheet */}
      <UserRoleSheet />

      {/* User Role History Modal */}
      <UserRoleHistoryModal />
    </div>
  );
}
